/**
 * The desktop sidebar's width is the user's: dragged at its edge, kept in localStorage, clamped
 * so it can neither vanish nor swallow the page. Pure helpers, so the numbers are testable
 * without a DOM.
 */
export const SIDEBAR_WIDTH_DEFAULT = 256; // 16rem — what the provider used before
export const SIDEBAR_WIDTH_MIN = 208;
export const SIDEBAR_WIDTH_MAX = 520;
export const SIDEBAR_WIDTH_KEYBOARD_STEP = 16;
export const SIDEBAR_WIDTH_STORAGE_KEY = 'tri.sidebar.width';

export function clampSidebarWidth(width: number): number {
    if (!Number.isFinite(width)) return SIDEBAR_WIDTH_DEFAULT;
    return Math.min(SIDEBAR_WIDTH_MAX, Math.max(SIDEBAR_WIDTH_MIN, Math.round(width)));
}

export function readStoredSidebarWidth(): number {
    try {
        const raw = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
        if (raw === null) return SIDEBAR_WIDTH_DEFAULT;
        return clampSidebarWidth(Number(raw));
    } catch {
        return SIDEBAR_WIDTH_DEFAULT;
    }
}

export function storeSidebarWidth(width: number): void {
    try {
        window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(clampSidebarWidth(width)));
    } catch {
        // private mode or blocked storage: the width still holds for this session
    }
}

/** Arrow keys on the handle: `side` decides which direction grows the sidebar. */
export function sidebarWidthAfterKey(width: number, key: string, side: 'left' | 'right'): number | null {
    const grow = side === 'left' ? 'ArrowRight' : 'ArrowLeft';
    const shrink = side === 'left' ? 'ArrowLeft' : 'ArrowRight';
    if (key === grow) return clampSidebarWidth(width + SIDEBAR_WIDTH_KEYBOARD_STEP);
    if (key === shrink) return clampSidebarWidth(width - SIDEBAR_WIDTH_KEYBOARD_STEP);
    if (key === 'Home') return SIDEBAR_WIDTH_MIN;
    if (key === 'End') return SIDEBAR_WIDTH_MAX;
    return null;
}
